/**
 * Trilingual UI strings for task builder and tasks page (legacy `frontend/`).
 * Mirrors keys added under `Tasks` in `frontend-next/messages/*.json`.
 */
export type TaskLocale = "en-US" | "ja-JP" | "ko-KR";

const STRINGS: Record<
  TaskLocale,
  {
    navTasks: string;
    pageTitle: string;
    pageSubtitle: string;
    empty: string;
    loading: string;
    error: string;
    dueLabel: string;
    noDue: string;
    completed: string;
    incomplete: string;
    openAction: string;
    markComplete: string;
    markIncomplete: string;
    taskBuilderTitle: string;
    taskTitlePlaceholder: string;
    taskDueLabel: string;
    addTask: string;
    removeTask: string;
    removePrinciple: string;
    createTaskCollectionLabel: string;
    createTaskCollectionDescription: string;
    taskCollectionTitleSuffix: string;
    taskCollectionCreateError: string;
    taskCollectionNameLabel: string;
    taskCollectionNamePlaceholder: string;
    taskCollectionNameRequired: string;
    principlesSectionTitle: string;
    principlesSectionSubtitle: string;
    principlesLoading: string;
    principlesError: string;
    principlesEmpty: string;
    addPrinciple: string;
    addPrincipleTooltip: string;
    principleAdded: string;
    principleCreateError: string;
    solutionPrinciplesTitle: string;
    solutionPrinciplesSubtitle: string;
    // ContextCommentPicker (shared by solution principles + action tasks)
    pickerSearchPlaceholderPrinciple: string;
    pickerSearchPlaceholderTask: string;
    pickerGroupYours: string;
    pickerGroupOthers: string;
    pickerCreateNewPrinciple: string;
    pickerCreateNewTask: string;
    pickerNoResults: string;
    pickerLoading: string;
    // Inline section on solution / action cards
    attachedPrinciplesTitle: string;
    attachedPrinciplesSubtitle: string;
    attachedPrinciplesEmpty: string;
    attachedTasksTitle: string;
    attachedTasksSubtitle: string;
    attachedTasksEmpty: string;
    unlink: string;
    unlinkConfirm: string;
    attachedSaveError: string;
  }
> = {
  "en-US": {
    navTasks: "Tasks",
    pageTitle: "My tasks",
    pageSubtitle: "Action tasks assigned to you",
    empty: "No tasks yet.",
    loading: "Loading tasks…",
    error: "Could not load tasks.",
    dueLabel: "Due",
    noDue: "No due date",
    completed: "Done",
    incomplete: "Open",
    openAction: "Open action",
    markComplete: "Mark done",
    markIncomplete: "Mark not done",
    taskBuilderTitle: "Tasks for this action",
    taskTitlePlaceholder: "Task description",
    taskDueLabel: "Due (optional)",
    addTask: "Add task",
    removeTask: "Remove task",
    removePrinciple: "Remove principle",
    createTaskCollectionLabel: "Create task collection",
    createTaskCollectionDescription: "Save the published task comments into a standard collection after the action is created.",
    taskCollectionTitleSuffix: "Tasks",
    taskCollectionCreateError: "The action was created, but the task collection could not be created.",
    taskCollectionNameLabel: "Collection name",
    taskCollectionNamePlaceholder: "Name this task collection",
    taskCollectionNameRequired: "A name is required for the task collection.",
    principlesSectionTitle: "Principles",
    principlesSectionSubtitle: "Guiding principles you want to keep visible on your profile.",
    principlesLoading: "Loading principles…",
    principlesError: "Could not load principles.",
    principlesEmpty: "No principles yet.",
    addPrinciple: "Add principle",
    addPrincipleTooltip: "Share a principle",
    principleAdded: "Principle added successfully.",
    principleCreateError: "Failed to add principle.",
    solutionPrinciplesTitle: "Add a principle",
    solutionPrinciplesSubtitle: "Share a principle this solution should follow.",
    pickerSearchPlaceholderPrinciple: "Search your principles or add a new one…",
    pickerSearchPlaceholderTask: "Search your tasks or add a new one…",
    pickerGroupYours: "Your {kind}",
    pickerGroupOthers: "Other {kind}",
    pickerCreateNewPrinciple: "Write a new principle",
    pickerCreateNewTask: "Write a new task",
    pickerNoResults: "No matching {kind}.",
    pickerLoading: "Searching…",
    attachedPrinciplesTitle: "Principles",
    attachedPrinciplesSubtitle: "Guiding principles this solution follows.",
    attachedPrinciplesEmpty: "No principles attached yet.",
    attachedTasksTitle: "Tasks",
    attachedTasksSubtitle: "What participants are expected to do.",
    attachedTasksEmpty: "No tasks attached yet.",
    unlink: "Remove",
    unlinkConfirm: "Remove this {kind}? It will stay on your profile.",
    attachedSaveError: "Could not save changes. Please try again.",
  },
  "ja-JP": {
    navTasks: "タスク",
    pageTitle: "自分のタスク",
    pageSubtitle: "アクションであなたに割り当てられたタスク",
    empty: "タスクはまだありません。",
    loading: "読み込み中…",
    error: "タスクを読み込めませんでした。",
    dueLabel: "期限",
    noDue: "期限なし",
    completed: "完了",
    incomplete: "未完了",
    openAction: "アクションを開く",
    markComplete: "完了にする",
    markIncomplete: "未完了に戻す",
    taskBuilderTitle: "このアクションのタスク",
    taskTitlePlaceholder: "タスクの内容",
    taskDueLabel: "期限（任意）",
    addTask: "タスクを追加",
    removeTask: "削除",
    removePrinciple: "原則を外す",
    createTaskCollectionLabel: "タスク用コレクションを作成",
    createTaskCollectionDescription: "アクション作成後に、公開されたタスクコメントを通常のコレクションとして保存します。",
    taskCollectionTitleSuffix: "タスク",
    taskCollectionCreateError: "アクションは作成されましたが、タスクコレクションを作成できませんでした。",
    taskCollectionNameLabel: "コレクション名",
    taskCollectionNamePlaceholder: "タスクコレクションに名前を付けてください",
    taskCollectionNameRequired: "タスクコレクションには名前が必要です。",
    principlesSectionTitle: "原則",
    principlesSectionSubtitle: "プロフィールに表示しておきたい、自分の指針となる原則です。",
    principlesLoading: "原則を読み込み中…",
    principlesError: "原則を読み込めませんでした。",
    principlesEmpty: "原則はまだありません。",
    addPrinciple: "原則を追加",
    addPrincipleTooltip: "原則を共有する",
    principleAdded: "原則を追加しました。",
    principleCreateError: "原則を追加できませんでした。",
    solutionPrinciplesTitle: "原則を追加",
    solutionPrinciplesSubtitle: "この解決策が従うべき原則を共有しましょう。",
    pickerSearchPlaceholderPrinciple: "自分の原則を検索または新しく追加…",
    pickerSearchPlaceholderTask: "自分のタスクを検索または新しく追加…",
    pickerGroupYours: "あなたの{kind}",
    pickerGroupOthers: "他の{kind}",
    pickerCreateNewPrinciple: "新しい原則を書く",
    pickerCreateNewTask: "新しいタスクを書く",
    pickerNoResults: "該当する{kind}はありません。",
    pickerLoading: "検索中…",
    attachedPrinciplesTitle: "原則",
    attachedPrinciplesSubtitle: "この解決策が従う指針となる原則です。",
    attachedPrinciplesEmpty: "まだ原則が追加されていません。",
    attachedTasksTitle: "タスク",
    attachedTasksSubtitle: "参加者に期待される行動です。",
    attachedTasksEmpty: "まだタスクが追加されていません。",
    unlink: "外す",
    unlinkConfirm: "この{kind}を外しますか？ あなたのプロフィールには残ります。",
    attachedSaveError: "変更を保存できませんでした。もう一度お試しください。",
  },
  "ko-KR": {
    navTasks: "작업",
    pageTitle: "내 작업",
    pageSubtitle: "액션에서 나에게 배정된 작업",
    empty: "아직 작업이 없습니다.",
    loading: "불러오는 중…",
    error: "작업을 불러올 수 없습니다.",
    dueLabel: "기한",
    noDue: "기한 없음",
    completed: "완료",
    incomplete: "진행 중",
    openAction: "액션 열기",
    markComplete: "완료로 표시",
    markIncomplete: "미완료로 표시",
    taskBuilderTitle: "이 액션의 작업",
    taskTitlePlaceholder: "작업 설명",
    taskDueLabel: "기한(선택)",
    addTask: "작업 추가",
    removeTask: "삭제",
    removePrinciple: "원칙 제거",
    createTaskCollectionLabel: "작업 컬렉션 만들기",
    createTaskCollectionDescription: "액션이 생성된 뒤 게시된 작업 댓글을 일반 컬렉션으로 저장합니다.",
    taskCollectionTitleSuffix: "작업",
    taskCollectionCreateError: "액션은 생성되었지만 작업 컬렉션은 만들지 못했습니다.",
    taskCollectionNameLabel: "컬렉션 이름",
    taskCollectionNamePlaceholder: "작업 컬렉션의 이름을 입력하세요",
    taskCollectionNameRequired: "작업 컬렉션에는 이름이 필요합니다.",
    principlesSectionTitle: "원칙",
    principlesSectionSubtitle: "프로필에 보이도록 남겨 두고 싶은 나의 기준과 원칙입니다.",
    principlesLoading: "원칙을 불러오는 중…",
    principlesError: "원칙을 불러올 수 없습니다.",
    principlesEmpty: "아직 원칙이 없습니다.",
    addPrinciple: "원칙 추가",
    addPrincipleTooltip: "원칙 공유하기",
    principleAdded: "원칙을 추가했습니다.",
    principleCreateError: "원칙을 추가하지 못했습니다.",
    solutionPrinciplesTitle: "원칙 추가",
    solutionPrinciplesSubtitle: "이 해결책이 따라야 할 원칙을 공유하세요.",
    pickerSearchPlaceholderPrinciple: "내 원칙 검색 또는 새로 추가…",
    pickerSearchPlaceholderTask: "내 작업 검색 또는 새로 추가…",
    pickerGroupYours: "내 {kind}",
    pickerGroupOthers: "다른 {kind}",
    pickerCreateNewPrinciple: "새 원칙 작성",
    pickerCreateNewTask: "새 작업 작성",
    pickerNoResults: "일치하는 {kind}이(가) 없습니다.",
    pickerLoading: "검색 중…",
    attachedPrinciplesTitle: "원칙",
    attachedPrinciplesSubtitle: "이 해결책이 따르는 지침이 되는 원칙입니다.",
    attachedPrinciplesEmpty: "아직 연결된 원칙이 없습니다.",
    attachedTasksTitle: "작업",
    attachedTasksSubtitle: "참여자에게 기대되는 행동입니다.",
    attachedTasksEmpty: "아직 연결된 작업이 없습니다.",
    unlink: "해제",
    unlinkConfirm: "이 {kind}을(를) 해제하시겠어요? 내 프로필에는 그대로 남습니다.",
    attachedSaveError: "변경을 저장하지 못했습니다. 다시 시도해 주세요.",
  },
};

/**
 * Resolves a supported locale from the browser.
 */
export function resolveTaskLocale(): TaskLocale {
  if (typeof navigator === "undefined" || !navigator.language) {
    return "en-US";
  }
  const lang = navigator.language.toLowerCase();
  if (lang.startsWith("ja")) {
    return "ja-JP";
  }
  if (lang.startsWith("ko")) {
    return "ko-KR";
  }
  return "en-US";
}

export function getTaskUiStrings(locale?: TaskLocale): (typeof STRINGS)["en-US"] {
  const key = locale ?? resolveTaskLocale();
  return STRINGS[key] ?? STRINGS["en-US"];
}
